import { useRef, useState } from "react";
import { toast } from "sonner";

import { useCreateActorMutation } from "Actors/Api/Mutations/useCreateActorMutation";
import { ActorForm, type ActorFormHandle } from "Actors/Forms/ActorForm";
import {
  type ActorFormData,
  toSaveActorRequest,
} from "Actors/Forms/actorFormSchema";
import {
  apiPut,
  getActorsActorID,
  getApiUrl,
  putUsersUserIDProfilePicturePath,
} from "Infrastructure/Api/Api";
import { isSuccessResponse } from "Infrastructure/Api/ApiUtils";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { BlModal } from "Shared/Dialogs/BlModal";

interface CreateActorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateActorModal = ({
  isOpen,
  onClose,
}: CreateActorModalProps): React.ReactNode => {
  const { t } = useResource();
  const formRef = useRef<ActorFormHandle | null>(null);
  const [pendingPicture, setPendingPicture] = useState<File | null>(null);

  const createActorMutation = useCreateActorMutation();

  const onSubmit = async (formData: ActorFormData) => {
    try {
      const result = await createActorMutation.mutateAsync(
        toSaveActorRequest(formData),
      );

      if (pendingPicture) {
        try {
          const actorDetail = await getActorsActorID(result.actorID);
          if (isSuccessResponse(actorDetail)) {
            const pictureData = new FormData();
            pictureData.append("file", pendingPicture);
            await apiPut(
              `${getApiUrl()}${putUsersUserIDProfilePicturePath(actorDetail.data.userID)}`,
              pictureData,
              undefined,
            );
          }
        } catch {
          // Picture upload failure should not block actor creation success
        }
      }

      toast.success(t(Resources.Actors.CreateActorModal.SuccessMessage));
      formRef.current?.reset();
      setPendingPicture(null);
      onClose();
    } catch {
      // error handled by mutation (field errors shown in form, non-field errors as toast)
    }
  };

  const handleClose = () => {
    formRef.current?.reset();
    setPendingPicture(null);
    onClose();
  };

  return (
    <BlModal
      isOpen={isOpen}
      title={t(Resources.Actors.CreateActorModal.Title)}
      onCancel={handleClose}
      onConfirm={() => formRef.current?.submit(onSubmit)()}
      cancelButtonText={t(Resources.Common.Cancel)}
      confirmButtonText={t(Resources.Actors.CreateActorModal.Create)}
      maxWidth="800px"
    >
      <ActorForm
        actor={null}
        formRef={formRef}
        pendingPicture={pendingPicture}
        onPendingPictureChange={setPendingPicture}
        serverError={createActorMutation.error}
      />
    </BlModal>
  );
};
